/**
 * PageBanner — global status strip driven by PageBannerProvider / usePageBanner.
 * Rendered in DashboardLayout directly below SyncBanner.
 */
import React from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { PageBannerContext } from './PageBannerProvider';

interface PageBannerProps {
  ml?: number | { xs?: number; md?: number };
  mr?: number | { xs?: number; md?: number };
}

export default function PageBanner({ ml, mr }: PageBannerProps) {
  const theme = useTheme();
  const { banner } = React.useContext(PageBannerContext);

  if (!banner) return null;

  const tokens = theme.tokens;
  const isWarning = banner.tone === 'warning';
  const accent = isWarning ? tokens.semantic.warning : tokens.semantic.info;
  const accentSoft = isWarning ? tokens.semantic.warningSoft : tokens.semantic.infoSoft;
  const titleColor = tokens.text.primary;

  const pulseDot = (
    <Box sx={{ flexShrink: 0, position: 'relative', width: 8, height: 8 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: accent,
          position: 'absolute',
          '@keyframes ping': {
            '0%': { transform: 'scale(1)', opacity: 0.7 },
            '75%, 100%': { transform: 'scale(2.2)', opacity: 0 },
          },
          animation: 'ping 1.4s ease-in-out infinite',
        }}
      />
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, position: 'absolute' }} />
    </Box>
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: ml ? (typeof ml === 'number' ? `${ml}px` : { xs: `${ml.xs ?? 0}px`, md: `${ml.md ?? 0}px` }) : undefined,
        mr: mr ? (typeof mr === 'number' ? `${mr}px` : { xs: `${mr.xs ?? 0}px`, md: `${mr.md ?? 0}px` }) : undefined,
        bgcolor: accentSoft,
        borderBottom: `1px solid ${tokens.border.subtle}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 0.75,
          alignItems: 'center',
          gap: 1.25,
          minHeight: 40,
        }}
      >
        {pulseDot}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <InfoOutlined sx={{ fontSize: 16, color: accent, flexShrink: 0 }} />
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ fontSize: '0.8125rem', color: titleColor, lineHeight: 1.4 }}
          >
            {banner.message}
          </Typography>
        </Stack>
      </Box>
      <LinearProgress
        variant="indeterminate"
        sx={{
          height: 2,
          bgcolor: tokens.border.subtle,
          '& .MuiLinearProgress-bar': { bgcolor: accent },
        }}
      />
    </Box>
  );
}
